import React, { useState } from "react"
import { useDispatch } from "react-redux"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { archiveEnterprise, reactivateEnterprise } from "../../../actions/Organizations/Enterprise"
import { openAppMessageSnackBar } from "../../../actions/Utils/app"
import { hasOngoingDeliveriesForEnterprise, hasPlanningRulesForEnterprise } from "../../../parseManager/organizations/deliveriesManager"

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 560,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "12px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
    },
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    p: "32px 40px 8px",
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    color: "rgba(0,0,0,0.87)",
    lineHeight: 1.3,
  },
  closeBtn: {
    width: 36,
    height: 36,
    color: "#555555",
    "&:hover": { bgcolor: "#F0F0F0" },
  },
  body: {
    p: "24px 40px 8px",
    fontSize: 16,
    color: "#414141",
    lineHeight: 1.55,
  },
  strong: {
    fontWeight: 500,
    color: "#000000",
  },
  serverError: {
    m: "8px 40px 0",
    fontSize: 16,
    lineHeight: 1.4,
    color: "#F44559",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1,
    p: "24px 40px 32px",
  },
  btnText: {
    color: "#2196F3",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    "&:hover": { bgcolor: "#E3F2FD" },
  },
  btnFilled: {
    bgcolor: "#2196F3",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    boxShadow: "0 2px 4px rgba(33,150,243,0.3)",
    "&:hover": {
      bgcolor: "#2196F3",
      boxShadow: "0 4px 8px rgba(33,150,243,0.4)",
    },
    "&.Mui-disabled": {
      color: "#FFFFFF",
      bgcolor: "#2196F3",
    },
  },
}

const ArchiveEnterpriseConfirmModal = ({ open, onClose, onBlocked, enterpriseId, enterpriseName, mode }) => {
  const dispatch = useDispatch()
  const [isSubmitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState(null)

  const isReactivate = mode === "reactivate"

  const handleClose = () => {
    if (isSubmitting) return
    setServerError(null)
    onClose()
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    setServerError(null)
    try {
      if (!isReactivate) {
        const hasPlanningRules = await hasPlanningRulesForEnterprise(enterpriseId)
        if (hasPlanningRules) {
          onClose()
          onBlocked("planningRules")
          return
        }
        const hasOngoingDeliveries = await hasOngoingDeliveriesForEnterprise(enterpriseId)
        if (hasOngoingDeliveries) {
          onClose()
          onBlocked("deliveries")
          return
        }
      }

      const succeeded = isReactivate
        ? await dispatch(reactivateEnterprise(enterpriseId))
        : await dispatch(archiveEnterprise(enterpriseId))

      onClose()
      if (succeeded) {
        dispatch(openAppMessageSnackBar(
          isReactivate
            ? `${enterpriseName} a bien été réactivée.`
            : `Cette action masquera ${enterpriseName} des listes d'entreprises actives, sans supprimer ses données. Vous pourrez la réactiver à tout moment depuis cette page.`
        ))
      }
    } catch {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  const ctaLabel = isReactivate ? "Réactiver" : "Archiver"
  const confirmLabel = isSubmitting
    ? (serverError ? "Nouvel essai…" : "Traitement…")
    : (serverError ? "Réessayer" : ctaLabel)

  return (
    <Dialog open={open} onClose={handleClose} sx={sx.dialog}>
      <Box sx={sx.header}>
        <Typography sx={sx.title}>
          {isReactivate
            ? "Souhaitez-vous réactiver cette entreprise ?"
            : "Souhaitez-vous archiver cette entreprise ?"}
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting} sx={sx.closeBtn} aria-label="Fermer">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography component="div" sx={sx.body}>
        {isReactivate ? (
          <>
            Cette action remettra <Box component="strong" sx={sx.strong}>{enterpriseName}</Box> dans les
            listes d&apos;entreprises actives. Elle pourra de nouveau recevoir des commandes.
          </>
        ) : (
          <>
            Cette action masquera <Box component="strong" sx={sx.strong}>{enterpriseName}</Box> des listes
            d&apos;entreprises actives ainsi que ses livraisons au statut Brouillon, sans supprimer ses
            données. Vous pourrez la réactiver à tout moment depuis cette page.
          </>
        )}
      </Typography>

      {serverError && (
        <Typography role="alert" sx={sx.serverError}>{serverError}</Typography>
      )}

      <Box sx={sx.footer}>
        <Button sx={sx.btnText} onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          sx={{ ...sx.btnFilled, ...(isSubmitting ? { opacity: 0.7, pointerEvents: "none" } : {}) }}
          onClick={handleConfirm}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Dialog>
  )
}

export default ArchiveEnterpriseConfirmModal
